import React, { useContext, useState } from 'react';
import { useTranslations } from 'use-intl';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { Link as HeaderLink } from 'components/commercetools-ui/organisms/header/types';
import Slideout, { MenuState } from 'components/commercetools-ui/organisms/header/utility-section/components/slide-out';
import CartIcon from 'components/icons/cart';
import WishlistIcon from 'components/icons/wishlist';
import { AccountContext } from 'context/account';
import { Cart, LineItem as CartLineItem, DiscountCode } from 'types/entity/cart';
import { Wishlist, LineItem as WishlistLineItem } from 'types/entity/wishlist';
import AccountButton from './components/account-button';
import StoreButton from './store-button';

export interface Props {
  cart?: Cart;
  isEmpty?: boolean;
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onRemoveDiscountCode?: (discount: DiscountCode) => Promise<void>;
  totalCartItems?: number;
  totalWishlistItems?: number;
  onRemoveItem(itemId: string): Promise<void>;
  onUpdateItem(itemId: string, quantity: number): Promise<void>;
  OnMoveToWishlist(lineItem: CartLineItem): Promise<void>;
  wishlist?: Wishlist;
  onRemoveFromWishlist?: (lineItemId: string) => Promise<void>;
  onMoveToCart?: (lineItem: WishlistLineItem) => Promise<void>;
  onClearWishlist?: () => Promise<void>;
  emptyCartTitle: string;
  emptyCartSubtitle: string;
  emptyCartImage: ImageProps;
  emptyCartCategories: HeaderLink[];
  emptyWishlistTitle: string;
  emptyWishlistSubtitle: string;
  emptyWishlistImage: ImageProps;
  emptyWishlistCategories: HeaderLink[];
  searchElement?: React.ReactNode;
}

const UtilitySection: React.FC<Props> = ({
  cart,
  isEmpty,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  onRemoveItem,
  onUpdateItem,
  OnMoveToWishlist,
  totalCartItems = 0,
  totalWishlistItems = 0,
  wishlist,
  onRemoveFromWishlist,
  onMoveToCart,
  onClearWishlist,
  emptyCartTitle,
  emptyCartSubtitle,
  emptyCartImage,
  emptyCartCategories,
  emptyWishlistTitle,
  emptyWishlistSubtitle,
  emptyWishlistImage,
  emptyWishlistCategories,
  searchElement,
}) => {
  const translate = useTranslations();
  const { loggedIn } = useContext(AccountContext);

  const [menuState, setMenuState] = useState<MenuState>();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const onWishlistClicked = () => {
    setIsDrawerOpen(true);
    setMenuState('wishlist');
  };

  const onCartClicked = () => {
    setIsDrawerOpen(true);
    setMenuState('cart');
  };

  return (
    <div className="mt-12 flex h-40 items-center">
      <div className="flex items-center justify-between gap-x-8 lg:gap-x-16">
        <div className="mb-12 hidden h-40 w-fit items-center xl:flex">
          <Link link={loggedIn ? '/membership/' : '/register/'} className="flex h-full items-center">
            <span className="rounded-lg bg-black px-12 py-8 text-12 font-medium uppercase text-white">
              {'Join Whoop'}
            </span>
          </Link>
        </div>
        {searchElement && <div className="h-40 w-fit">{searchElement}</div>}
        <AccountButton />
        <StoreButton />

        <div className="h-40 w-fit">
          <button
            aria-label={translate('wishlist.my-wishlist')}
            className="relative h-fit cursor-pointer border-black pb-8 hover:border-b-2"
            onClick={onWishlistClicked}
          >
            <div className="flex items-center gap-6">
              <WishlistIcon
                totalWishlistItems={totalWishlistItems}
                className="size-20 shrink-0 text-black 2xl:size-28"
              />
              <span className="hidden text-10 font-medium uppercase text-black lg:inline 2xl:text-12">
                {'Wishlist'}
              </span>
            </div>
          </button>
        </div>

        <div className="h-40 w-fit">
          <button
            aria-label={translate('cart.myCart')}
            className="h-fit cursor-pointer border-black pb-8 hover:border-b-2"
            onClick={onCartClicked}
          >
            <div className="lg:hidden">
              <CartIcon totalCartItems={totalCartItems} />
            </div>
            <div className="hidden items-center gap-6 lg:flex">
              <svg
                className="size-20 shrink-0 2xl:size-28"
                fill="black"
                focusable="false"
                role="presentation"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>{'Cart'}</title>
                <path
                  clipRule="evenodd"
                  d="M0.819824 2C0.819824 1.44772 1.26754 1 1.81982 1H4.51982C5.45524 1 6.26571 1.64837 6.47104 2.56097L7.01982 5H28.6216C29.8694 5 30.8126 6.13009 30.5894 7.35777L29.2516 14.7155C28.9058 16.6175 27.2492 18 25.3161 18H9.94482L10.093 18.6585C10.401 20.0274 11.6167 21 13.0198 21H27.8198C28.3721 21 28.8198 21.4477 28.8198 22C28.8198 22.5523 28.3721 23 27.8198 23H13.0198C10.6813 23 8.65511 21.3791 8.14178 19.0976L4.51982 3H1.81982C1.26754 3 0.819824 2.55228 0.819824 2ZM9.49482 16H25.3161C26.2827 16 27.1109 15.3088 27.2838 14.3578L28.6216 7H7.46982L9.49482 16Z"
                  fillRule="evenodd"
                ></path>
                <path
                  clipRule="evenodd"
                  d="M14.8198 27.5C14.8198 29.433 13.2528 31 11.3198 31C9.38683 31 7.81982 29.433 7.81982 27.5C7.81982 25.567 9.38683 24 11.3198 24C13.2528 24 14.8198 25.567 14.8198 27.5ZM12.8198 27.5C12.8198 28.3284 12.1483 29 11.3198 29C10.4914 29 9.81982 28.3284 9.81982 27.5C9.81982 26.6716 10.4914 26 11.3198 26C12.1483 26 12.8198 26.6716 12.8198 27.5Z"
                  fillRule="evenodd"
                ></path>
                <path
                  clipRule="evenodd"
                  d="M28.8198 27.5C28.8198 29.433 27.2528 31 25.3198 31C23.3868 31 21.8198 29.433 21.8198 27.5C21.8198 25.567 23.3868 24 25.3198 24C27.2528 24 28.8198 25.567 28.8198 27.5ZM26.8198 27.5C26.8198 28.3284 26.1483 29 25.3198 29C24.4914 29 23.8198 28.3284 23.8198 27.5C23.8198 26.6716 24.4914 26 25.3198 26C26.1483 26 26.8198 26.6716 26.8198 27.5Z"
                  fillRule="evenodd"
                ></path>
              </svg>
              <span className="flex items-center gap-8 text-10 font-medium uppercase text-black 2xl:text-12">
                {'Cart '}
                <span>{`(${totalCartItems})`}</span>
              </span>
            </div>
          </button>
        </div>
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        direction="right"
        className="w-[90%] max-w-380 bg-white"
        onClose={() => setIsDrawerOpen(false)}
      >
        <Slideout
          cart={cart}
          isEmpty={isEmpty}
          onApplyDiscountCode={onApplyDiscountCode}
          onRemoveDiscountCode={onRemoveDiscountCode}
          onRemoveItem={onRemoveItem}
          onUpdateItem={onUpdateItem}
          OnMoveToWishlist={OnMoveToWishlist}
          wishlist={wishlist}
          onRemoveFromWishlist={onRemoveFromWishlist}
          onMoveToCart={onMoveToCart}
          onClearWishlist={onClearWishlist}
          state={menuState}
          onClose={() => setIsDrawerOpen(false)}
          changeState={(newState) => setMenuState(newState)}
          emptyCartTitle={emptyCartTitle}
          emptyCartSubtitle={emptyCartSubtitle}
          emptyCartImage={emptyCartImage}
          emptyCartCategories={emptyCartCategories}
          emptyWishlistTitle={emptyWishlistTitle}
          emptyWishlistSubtitle={emptyWishlistSubtitle}
          emptyWishlistImage={emptyWishlistImage}
          emptyWishlistCategories={emptyWishlistCategories}
        />
      </Drawer>
    </div>
  );
};

export default UtilitySection;
